var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create } from "zustand";
import { sendAjaxRequest } from "../API";
import { ClientDetailCode, AgeVerificationStatus, } from "../interface";
// Re-export types for consumers that import from this store
export { AgeVerificationStatus };
const LOGPREFIX = "AgeVerificationStore: ";
export const useAgeVerificationStore = create()((set, get) => ({
    loading: false,
    error: undefined,
    metadata: undefined,
    loadStatus: (userId, auth, cacheBuster, logger) => __awaiter(void 0, void 0, void 0, function* () {
        const state = get();
        if (state.loading) {
            return;
        }
        if (!auth.bearer || !auth.sequence || !userId) {
            logger.error(LOGPREFIX + "Missing auth credentials");
            set({ error: "Not authenticated" });
            return;
        }
        set({ loading: true, error: undefined });
        const payload = {
            operation: "getStatus",
            userId,
            sequence: auth.sequence,
        };
        sendAjaxRequest((ok, status, message) => {
            try {
                if (ok) {
                    const response = JSON.parse(message);
                    // Update auth with new bearer and sequence
                    if (response.bearer && response.sequence) {
                        auth.setBearerAndSequence(response.bearer, Number(response.sequence), logger);
                    }
                    // Store presigned URL if provided (for unverified users)
                    const presignedUrl = response.presignedUrl;
                    const presignedUrlTimestamp = presignedUrl
                        ? Date.now()
                        : undefined;
                    if (presignedUrl) {
                        logger.log(LOGPREFIX + "Received presigned URL for image upload");
                    }
                    set({
                        loading: false,
                        error: undefined,
                        metadata: response.metadata || undefined,
                        presignedUrl,
                        presignedUrlTimestamp,
                    });
                    logger.log(LOGPREFIX + "Status loaded successfully");
                }
                else {
                    let errorMessage = "Failed to load verification status";
                    try {
                        const errorResponse = JSON.parse(message);
                        errorMessage = errorResponse.error || errorMessage;
                    }
                    catch (_a) {
                        // Use default error message
                    }
                    logger.error(LOGPREFIX + "Error loading status: " + errorMessage);
                    set({
                        loading: false,
                        error: errorMessage,
                        metadata: undefined,
                        presignedUrl: undefined,
                        presignedUrlTimestamp: undefined,
                    });
                }
            }
            catch (error) {
                logger.error(LOGPREFIX + "Exception: " + error);
                set({
                    loading: false,
                    error: "An unexpected error occurred",
                    metadata: undefined,
                    presignedUrl: undefined,
                    presignedUrlTimestamp: undefined,
                });
            }
        }, "ageVerification", payload, auth.bearer, cacheBuster, logger);
    }),
    submitVerification: (declaredAge, selfieImage, auth, cacheBuster, logger) => __awaiter(void 0, void 0, void 0, function* () {
        const state = get();
        if (state.loading) {
            return false;
        }
        if (!auth.bearer || !auth.sequence || !auth.userId) {
            logger.error(LOGPREFIX + "Missing auth credentials");
            set({ error: "Not authenticated" });
            return false;
        }
        set({ loading: true, error: undefined });
        try {
            // Step 1: Ensure we have a valid presigned URL
            let presignedUrl = state.presignedUrl;
            const urlAge = state.presignedUrlTimestamp
                ? Date.now() - state.presignedUrlTimestamp
                : Number.MAX_SAFE_INTEGER;
            const URL_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes (less than the 15-minute server expiry)
            if (!presignedUrl || urlAge > URL_EXPIRY_MS) {
                logger.log(LOGPREFIX + "No valid presigned URL, loading status to get one...");
                // Call loadStatus to get a fresh presigned URL
                yield get().loadStatus(auth.userId, auth, cacheBuster, logger);
                // Get the updated state
                const updatedState = get();
                presignedUrl = updatedState.presignedUrl;
                if (!presignedUrl) {
                    logger.error(LOGPREFIX + "Failed to get presigned URL from status");
                    set({
                        loading: false,
                        error: "Failed to get upload URL. Please try refreshing the page.",
                    });
                    return false;
                }
            }
            // Step 2: Upload image directly to S3
            logger.log(LOGPREFIX + "Uploading image to S3...");
            const uploadResponse = yield fetch(presignedUrl, {
                method: "PUT",
                body: selfieImage,
                headers: {
                    "Content-Type": "image/jpeg",
                },
            });
            if (!uploadResponse.ok) {
                logger.error(LOGPREFIX +
                    "Failed to upload image to S3: " +
                    uploadResponse.statusText);
                set({
                    loading: false,
                    error: "Failed to upload image",
                });
                return false;
            }
            logger.log(LOGPREFIX + "Image uploaded successfully, submitting verification...");
            // Step 3: Submit verification (backend will find the uploaded image)
            const payload = {
                operation: "submitVerification",
                userId: auth.userId,
                sequence: auth.sequence,
                verificationData: {
                    declaredAge,
                },
            };
            return new Promise((resolve) => {
                sendAjaxRequest((ok, status, message) => {
                    try {
                        if (ok) {
                            const response = JSON.parse(message);
                            // Update auth with new bearer and sequence
                            if (response.bearer && response.sequence) {
                                auth.setBearerAndSequence(response.bearer, Number(response.sequence), logger);
                            }
                            // Clear presigned URL since verification is complete
                            set({
                                loading: false,
                                error: undefined,
                                metadata: response.metadata,
                                presignedUrl: undefined,
                                presignedUrlTimestamp: undefined,
                            });
                            logger.log(LOGPREFIX + "Verification submitted successfully");
                            resolve(true);
                        }
                        else {
                            let errorMessage = "Verification failed";
                            let clientDetailCode = 0;
                            try {
                                const errorResponse = JSON.parse(message);
                                errorMessage = errorResponse.error || errorMessage;
                                clientDetailCode = errorResponse.clientDetailCode;
                            }
                            catch (_a) {
                                // Use default error message
                            }
                            // Check for specific error codes
                            if (clientDetailCode ===
                                ClientDetailCode.AGE_VERIFICATION_UNDER_AGE) {
                                errorMessage =
                                    "You must be 18 or older to verify your age.";
                            }
                            else if (clientDetailCode ===
                                ClientDetailCode.AGE_VERIFICATION_INVALID_IMAGE) {
                                errorMessage =
                                    "Please ensure your face is clearly visible in the image.";
                            }
                            else if (clientDetailCode ===
                                ClientDetailCode.AGE_VERIFICATION_IMAGE_TOO_OLD) {
                                errorMessage =
                                    "Please take a new photo. The image must be recent.";
                            }
                            else if (clientDetailCode ===
                                ClientDetailCode.AGE_VERIFICATION_S3_ERROR) {
                                errorMessage = "Image processing failed. Please try again.";
                            }
                            logger.error(LOGPREFIX + "Verification error: " + errorMessage);
                            set({
                                loading: false,
                                error: errorMessage,
                            });
                            resolve(false);
                        }
                    }
                    catch (error) {
                        logger.error(LOGPREFIX + "Exception in verification: " + error);
                        set({
                            loading: false,
                            error: "An unexpected error occurred",
                        });
                        resolve(false);
                    }
                }, "ageVerification", payload, auth.bearer, cacheBuster, logger);
            });
        }
        catch (error) {
            logger.error(LOGPREFIX + "Error in verification workflow: " + error);
            set({
                loading: false,
                error: "Failed to process verification",
            });
            return false;
        }
    }),
    reset: () => {
        set({
            loading: false,
            error: undefined,
            metadata: undefined,
            presignedUrl: undefined,
            presignedUrlTimestamp: undefined,
        });
    },
}));
